import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { AuthService } from "@skylab-angular-shared/config";
import { QueryBus } from "@skylab-angular-shared/core";
import { TenantAppState, TenantAppStateQuery, TenantAppStateResponse } from "@skylab-shared/business";
import { AppStates } from "@skylab-shared/scenes";
import { AppInitDataService } from "./app-init.service";

export const appInitGuard = (): CanActivateFn => {
	return async () => {
		const initDataService = inject(AppInitDataService);
		const router = inject(Router);
		const queryBus = inject(QueryBus);
		const authService = inject(AuthService);

		const result = await queryBus.queryAsync(new TenantAppStateQuery());

		if (result.status === 'Failed') {
			throw new Error('App state loading failed!');
		}

		if (result.status === 'Unauthorized') {
			authService.logout();
			return false;
		};

		const state = getAppStateConfig(result);
		if (state?.code === TenantAppState.Configured) return await initDataService.run();

		if (state?.code === TenantAppState.KIRestricted) {
			window.location.href = state.params?.['url'];
		} else {
			router.navigate([`${state?.url}`], { info: { redirectFromSuccessfullAuth: true }, queryParams: state?.params });
		}

		return false;
	};
}

function getAppStateConfig(result: TenantAppStateResponse): { code: TenantAppState, url: string | null, params?: {[key: string]: any} } | null {
	try {
		const stateMap: Record<TenantAppState, string | null> = {
			[TenantAppState.AccountDataRemoving]: AppStates.accountDataRemoving.url,
			[TenantAppState.Maintenance]: AppStates.maintenance.url,
			[TenantAppState.Unconfigured]: AppStates.initialConfiguration.url,
			[TenantAppState.InProgress]: AppStates.initialConfiguration.url,
			[TenantAppState.Failed]: AppStates.unexpectedError.url,
			[TenantAppState.KIRestricted]: AppStates.unexpectedError.url,
			[TenantAppState.Configured]: null
		}

		if (result.data) {
			return {
				code: result.data.code,
				url: stateMap[result.data.code],
				params: result.data.data
			};
		}

		return null;
	} catch (err) {
		throw new Error('App state loading failed!', err as any);
	}
}
